/* eslint-disable @next/next/no-img-element */
import React, { useState } from 'react';
import { useLessonPlayer } from '@stores/lessonPlayerStore';
import Image from '@base/Image';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Head from 'next/head';
import videoSchema from '@seo/schemas/unacademyIcons/videoSchema';
import { SEOScript } from '@seo/index';
import Typography from '@unicons-base/Typography/Typography';
import { getImage } from '@utils/imageHelper';
import { SPACING_6B } from '@styles/constants/spacings';
import {
  LAUNCH_TEASER_VIDEO,
  UA_ICONS_SLUG
} from '@constants/unacademyIcons/icons.constants';
import Button from '@unicons-base/Button/Button';
import DangerousP2 from '@unicons-base/DangerousP2/DangerousP2';
import useWindowSize from '@hooks/useWindowSize';
import useTimeout from '@hooks/useTimeout';
import { PLAYER_Z_INDEX } from '@constants/unacademyIcons/style.constants';
import BlackPlay from '@icon/BlackPlay';
import PlayOutline from '@icon/PlayOutline';
import ShareOutline from '@icon/ShareOutline';
import Link from 'next/link';
import { emptyObject } from '@constants/empty';
import usePageHead from '@cont/PreSubscribed/usePageHead';
import { UN_ICONS_BUTTON_VARIANT } from 'types/unacademyIcons/button.type';
import Dialog from '@comp/Dialog/Dialog';
import ShareDialog from '@comp/ShareDialog/ShareDialog';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import WatchTrailer from '../WatchTrailer/WatchTrailer';

interface HeaderProps {
  videoSrc: string;
  coverMain: string;
  title: string;
  description: string;
  titleCover: string;
  courseSlug: string;
  hasActiveSubscription: boolean;
  isComingSoon?: boolean;
  teaser: string;
  continueWatching: any;
  firstLessonSlug?: string | null;
  iconGoalUID: string | null;
  pricingInfo: { title: string; description: string };
}

const StyledHeader = styled.header`
  position: relative;
  overflow: hidden;
  ${mediaQuery.uptoTablet} {
    overflow: visible;
  }
`;

const Video = styled.video`
  width: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  margin-top: -4px;
  ${mediaQuery.uptoTablet} {
    height: 100%;
  }
  ${mediaQuery.uptoMobile} {
    height: 80%;
  }
`;

const HeaderContent = styled.div`
  text-align: center;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: flex-end;
  bottom: 0px;
  padding: 0px ${Spacings.SPACING_3B} 80px;
  z-index: 2;
  ${mediaQuery.uptoTablet} {
    padding: 0px ${Spacings.SPACING_4B};
    margin-top: 0px;
    height: 100%;
    margin-bottom: 40px;
  }
`;

const TeaserWrapper = styled.div<{ $visible: boolean }>`
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 50vh;
  margin-top: 0;
  background-image: ${({ $visible }) =>
    !$visible && `url(${getImage('icon-course-banner.png')})`};
  background-size: contain;
  padding-top: ${Spacings.SPACING_8B};
  min-width: 100%;
  min-height: 100%;
  ${mediaQuery.uptoTablet} {
    background-size: cover;
    margin-top: ${Spacings.SPACING_12B};
    height: auto;
  }
`;

type TeaserType = {
  $visible: boolean;
};

const TeaserOverlay = styled.div<TeaserType>`
  z-index: 1;
  background: linear-gradient(
    179.56deg,
    rgba(0, 0, 0, 0) 40.03%,
    rgb(0, 0, 0) 81.62%
  );
  position: absolute;
  width: 100%;
  bottom: ${({ $visible }) => $visible && 0};
  height: ${({ $visible }) => ($visible ? '100vh' : '50vh')};
  ${mediaQuery.uptoMobile} {
    height: ${({ $visible }) => ($visible ? '100vh' : '20vh')};
  }
`;

const StyledDescription = styled(Typography)`
  z-index: ${PLAYER_Z_INDEX.BUTTON};
  color: var(--color-i-icon-dark-secondary);
  font-size: ${Fonts.LARGE};
  font-weight: ${Fonts.REGULAR};
  margin-top: ${SPACING_6B};
  margin-bottom: 0;
  max-width: 702px;
  line-height: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H6};
    line-height: ${Spacings.SPACING_18};
    margin-top: ${Spacings.SPACING_4B};
    font-weight: ${Fonts.REGULAR} !important;
    margin-bottom: 0;
  }
`;

const SubscribedButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 328px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const StyledButton = styled(Button)`
  z-index: ${PLAYER_Z_INDEX.BUTTON};
  font-size: ${Fonts.H6};
  width: 328px;
  font-weight: ${Fonts.BOLD};
  margin-top: ${Spacings.SPACING_6B};
  background: linear-gradient(
    261.69deg,
    #aa823d -0.19%,
    #efe288 59.57%,
    #d1b85a 85.24%
  );
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin: auto;
    margin-top: ${Spacings.SPACING_6B};
    margin-bottom: 0;
  }
`;

const StyledSecondaryButton = styled(StyledButton)<{ $hasMargin?: boolean }>`
  color: var(--color-i-icon-dark-secondary);
  margin-right: ${({ $hasMargin }) => ($hasMargin ? Spacings.SPACING_4B : '')};
  background: var(--color-i-icon-dark-primary);
  margin-top: ${Spacings.SPACING_4B};
  &:hover {
    box-shadow: none;
  }
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
    margin-right: ${({ $hasMargin }) => $hasMargin && Spacings.SPACING_4B};
  }
`;

const StyledComingSoonButton = styled(StyledButton)`
  cursor: auto;
  width: 328px;
  &:hover {
    transform: none;
    box-shadow: none;
  }
`;

const CoverWrapper = styled.div`
  position: relative;
  z-index: ${PLAYER_Z_INDEX.BUTTON};
`;

const StyledTrailerButton = styled(Button)`
  position: absolute;
  top: -130px;
  left: 0;
  right: 0;
  border: none;
  width: 120px;
  height: 120px;
  min-height: 120px;
  display: flex;
  margin: auto;
  &:hover {
    background-color: transparent;
    box-shadow: none;
  }
`;

const BackgroundOverlay = styled.div`
  background: linear-gradient(
    179.71deg,
    #777ba5 5.46%,
    rgba(43, 45, 53, 0) 95.01%
  );
  opacity: 0.2;
  height: 60vh;
  width: 100%;
  position: absolute;
  top: 0;
  ${mediaQuery.uptoTablet} {
    height: 20vh;
  }
`;

const StyledImage = styled(Image)`
  opacity: ${({ $visible }) => !$visible && '0'};
  z-index: 0;
`;

const StyledCover = styled.img`
  z-index: ${PLAYER_Z_INDEX.BUTTON};
`;

const Header: React.FC<HeaderProps> = ({
  videoSrc,
  title,
  description,
  titleCover,
  coverMain,
  hasActiveSubscription,
  isComingSoon,
  continueWatching,
  firstLessonSlug,
  courseSlug,
  teaser,
  iconGoalUID,
  pricingInfo
}) => {
  const [isVideoPlayed, setVideoPlayed] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [isTrailerVisible, setTrailer] = useState(false);
  const { isMobile } = useWindowSize();
  const { title: pricingTitle, description: pricingDescription } =
    pricingInfo || emptyObject;
  const resetLessonPlayer = useLessonPlayer.use.resetLessonPlayer();
  const setFullscreen = useLessonPlayer.use.setFullscreen();
  const { selectedGoal } = useSelectedGoal();

  useTimeout(() => setVideoPlayed(true), 2000);

  const closeShareOptions = () => setShowShareOptions(false);

  const openShareOptions = () => setShowShareOptions(true);

  const shareDialogData = {
    name: 'unacademy icons'
  };

  const { handleSubscriptionClick } = usePageHead({
    goalSlug: UA_ICONS_SLUG,
    goalUID: iconGoalUID,
    goalDetails: selectedGoal,
    isSubscribed: hasActiveSubscription
  });
  const handleClose = () => {
    resetLessonPlayer();
    setTrailer(false);
    setFullscreen(false);
  };
  const handleOpen = () => {
    setTrailer(true);
    setFullscreen(true);
  };

  return (
    <>
      <Head>
        {SEOScript({
          schema: videoSchema()
        })}
      </Head>
      {/* {!isVideoPlayed && <BackgroundOverlay />} */}
      <StyledHeader>
        {isVideoPlayed && (
          <Video loop muted autoPlay playsInline>
            <source src={LAUNCH_TEASER_VIDEO} type="video/mp4" />
            <source src={LAUNCH_TEASER_VIDEO} type="video/webm" />
          </Video>
        )}

        <HeaderContent>
          {/* Main Cover: Before teaser starts playing */}
          <TeaserWrapper $visible={isVideoPlayed}>
            <StyledImage
              $visible={!isVideoPlayed}
              src={coverMain}
              alt={title}
              layout="intrinsic"
              width={isMobile ? 169 : 330}
              height={isMobile ? 213 : 419}
            />
            <TeaserOverlay $visible={isVideoPlayed} />
          </TeaserWrapper>

          <CoverWrapper>
            <StyledCover
              src={titleCover}
              alt={title}
              width={194}
              height={isMobile ? 85 : 88}
            />
            {!hasActiveSubscription && (
              <StyledTrailerButton
                variant={UN_ICONS_BUTTON_VARIANT.HOLLOW}
                bgColor="transparent"
                onClick={handleOpen}
              >
                <StyledImage
                  $visible
                  src="watch-trailer.svg"
                  alt="Watch Trailer"
                  layout="fill"
                />
              </StyledTrailerButton>
            )}
          </CoverWrapper>

          <StyledDescription>{description}</StyledDescription>
          {!hasActiveSubscription && (
            <StyledButton onClick={() => handleSubscriptionClick()}>
              {pricingTitle}
            </StyledButton>
          )}
          {hasActiveSubscription && !isComingSoon && (
            <Link
              href={`/icons/courses/${courseSlug}/lessons/${
                continueWatching?.slug || firstLessonSlug
              }`}
              passHref
            >
              <StyledButton icon={<BlackPlay />}>
                {continueWatching?.uuid ? 'Resume' : 'Start'} lesson{' '}
                {continueWatching?.order || '1'}
              </StyledButton>
            </Link>
          )}

          {hasActiveSubscription && isComingSoon && (
            <StyledComingSoonButton
              icon={
                <Image
                  src="black-lock.svg"
                  width={20}
                  alt="Lock"
                  height={20}
                  layout="intrinsic"
                />
              }
            >
              Coming soon
            </StyledComingSoonButton>
          )}

          {hasActiveSubscription && (
            <SubscribedButtonsWrapper>
              <StyledSecondaryButton
                bgColor="var(--color-i-icon-dark-primary)"
                $hasMargin
                icon={<PlayOutline />}
                onClick={handleOpen}
              >
                Watch trailer
              </StyledSecondaryButton>
              <StyledSecondaryButton
                bgColor="var(--color-i-icon-dark-primary)"
                icon={<ShareOutline />}
                onClick={openShareOptions}
              >
                Share
              </StyledSecondaryButton>
            </SubscribedButtonsWrapper>
          )}
          {!hasActiveSubscription && (
            <DangerousP2 paragraph={pricingDescription} />
          )}
        </HeaderContent>
        {/* Watch Trailer */}
        <WatchTrailer
          src={videoSrc}
          open={isTrailerVisible}
          handleClose={handleClose}
        />
        <Dialog onClose={closeShareOptions} dialogState={showShareOptions}>
          <ShareDialog
            data={shareDialogData}
            iconBackground="var(--color-i-icons-gradient)"
            iconColor="var(--color-i-icon-dark-tertiary)"
            copyColor="var(--color-i-icon-gold)"
          />
        </Dialog>
      </StyledHeader>
    </>
  );
};

Header.displayName = 'Header';

export default Header;
